import { Injectable, inject } from '@angular/core';
import { Observable, map, of, switchMap, tap } from 'rxjs';
import { ProductService } from '@core';
import type { ProductCartWithEngraving, ProductDetails, ProductDetailsWithVariant, RelatedProduct } from '@models';
import { usdToPoints } from '@models';
import { ApiService } from './api-service';

import type { AppleCareOption } from '../../../../ui/apple-components/applecare-selection/applecare-selection.component';

export interface AppleCareData {
  hasAppleCare: boolean;
  appleCareOptions: AppleCareOption[];
  appleCareProduct: ProductDetailsWithVariant | null;
}

export interface AppleCareSelection {
  selectedOption: AppleCareOption;
  isAppleCareAdded: boolean;
  currentSelectedOptionId: string;
}

// Cache interface for AppleCare data
interface CachedAppleCareData {
  data: AppleCareData;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProductAppleCareService {
  private productService = inject(ProductService);
  private apiService = inject(ApiService);

  // AppleCare data cache with 10-minute timeout
  private appleCareCache = new Map<string, CachedAppleCareData>();
  private readonly CACHE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

  /**
   * Cache utility methods
   */
  private isCacheValid(cachedItem: CachedAppleCareData): boolean {
    return Date.now() - cachedItem.timestamp < this.CACHE_TIMEOUT_MS;
  }

  private getCachedAppleCare(productId: string): AppleCareData | null {
    const cached = this.appleCareCache.get(productId);
    if (cached && this.isCacheValid(cached)) {
      return cached.data;
    } else if (cached) {
      this.appleCareCache.delete(productId);
    }
    return null;
  }

  private setCachedAppleCare(productId: string, data: AppleCareData): void {
    const cachedData: CachedAppleCareData = {
      data,
      timestamp: Date.now(),
    };
    this.appleCareCache.set(productId, cachedData);
  }

  /**
   * Clear expired cache entries
   */
  clearExpiredAppleCareCache(): void {
    const now = Date.now();
    let removedCount = 0;

    for (const [productId, cached] of this.appleCareCache.entries()) {
      if (!this.isCacheValid(cached)) {
        this.appleCareCache.delete(productId);
        removedCount++;
      }
    }
  }

  /**
   * Get cache statistics
   */
  getAppleCareCacheStats(): { cacheSize: number; validEntries: number } {
    let validEntries = 0;

    for (const cached of this.appleCareCache.values()) {
      if (this.isCacheValid(cached)) {
        validEntries++;
      }
    }

    return {
      cacheSize: this.appleCareCache.size,
      validEntries,
    };
  }

  isProductWithVariants(product: ProductDetails): product is ProductDetailsWithVariant {
    return 'similarProducts' in product && Array.isArray((product as any).similarProducts);
  }

  isProductCartWithEngraving(product: any): product is ProductCartWithEngraving {
    return product && ('varianceSelections' in product || 'varianceSelectionsJson' in product);
  }

  isAppleCareProduct(): Observable<number[]> {
    // RCP-306: AppleCare is not offered for the event, so there are no AppleCare products to look up.
    // Restore the request below for events that sell AppleCare.
    return of([]);
    // return this.apiService.get(`products?categoryIds=60000&Count=100`).pipe(
    //   map((response: any) => {
    //     const items = response?.items ?? [];
    //     return items.map((item: any) => item.productId);
    //   }),
    // );
  }

  checkForAppleCare(productId: string): Observable<AppleCareData> {
    // Check cache first
    const cached = this.getCachedAppleCare(productId);
    if (cached) {
      return of(cached);
    }

    // Try to get from ProductService cache first (avoids API call if product already loaded)
    const productCacheData = this.productService.getCachedProductData(productId);
    if (productCacheData) {
      return this.processAppleCareFromCachedData(productId, productCacheData.relatedProducts);
    }

    // Fallback to API call through ProductService (which will cache the result)
    return this.productService
      .getRelatedProducts(productId)
      .pipe(switchMap((relatedProducts) => this.processAppleCareFromCachedData(productId, relatedProducts)));
  }

  /**
   * Process AppleCare data from cached related products
   */
  private processAppleCareFromCachedData(
    productId: string,
    relatedProducts: RelatedProduct[],
  ): Observable<AppleCareData> {
    const appleCareProduct = relatedProducts.find((product) => product.name?.toLowerCase().includes('applecare'));

    if (appleCareProduct) {
      // Check if we have the full AppleCare product details in cache
      const appleCareDetailsCache = this.productService.getCachedProductData(appleCareProduct.productId.toString());

      if (appleCareDetailsCache) {
        const result = this.buildAppleCareResult(productId, appleCareProduct, appleCareDetailsCache.productDetails);
        return of(result);
      }

      // Fetch the full product details for AppleCare if not cached
      return this.productService.getProductByProdID(appleCareProduct.productId.toString()).pipe(
        map((productDetails: ProductDetails) => {
          const result = this.buildAppleCareResult(productId, appleCareProduct, productDetails);
          return result;
        }),
      );
    } else {
      // No AppleCare product found
      const result: AppleCareData = {
        hasAppleCare: false,
        appleCareOptions: [
          {
            id: 'no-applecare',
            modelName: 'None',
            title: 'No AppleCare+ Coverage',
            price: '$0.00',
            isAppleCare: false,
            features: [],
          },
        ],
        appleCareProduct: null,
      };

      // Cache the result
      this.setCachedAppleCare(productId, result);
      return of(result);
    }
  }

  /**
   * Build AppleCare result from product data
   */
  private buildAppleCareResult(
    productId: string,
    appleCareProduct: RelatedProduct,
    productDetails: ProductDetails,
  ): AppleCareData {
    // Use the type guard to check if it's ProductDetailsWithVariant
    const appleCareProductDetails = this.isProductWithVariants(productDetails) ? productDetails : null;

    const options: AppleCareOption[] = [];
    const appleCarePoints = usdToPoints(appleCareProduct.price ?? 0);

    const mappedAppleCare: AppleCareOption = {
      id: `applecare-${appleCareProduct.productId}`,
      modelName: appleCareProduct.name,
      title: appleCareProduct.name,
      price: `${(appleCareProduct.price ?? 0).toFixed(2)}`,
      points: appleCarePoints.toFixed(0),
      isAppleCare: true,
      features: [
        'Unlimited repairs for accidental damage protection',
        'Apple certified repairs using genuine Apple parts',
        "Express Replacement Service -- we'll ship you a replacement so you don't have to wait for a repair",
        '24/7 priority access to Apple experts',
      ],
    };

    options.push(mappedAppleCare);

    // Always append "No AppleCare" option
    options.push({
      id: 'no-applecare',
      modelName: 'None',
      title: 'No AppleCare+ Coverage',
      price: '$0.00',
      isAppleCare: false,
      features: [],
    });

    const result: AppleCareData = {
      hasAppleCare: true,
      appleCareOptions: options,
      appleCareProduct: appleCareProductDetails,
    };

    // Cache the result
    this.setCachedAppleCare(productId, result);

    return result;
  }

  /**
   * Process AppleCare selection change
   */
  processAppleCareSelection(selectedOption: AppleCareOption): AppleCareSelection {
    return {
      selectedOption,
      isAppleCareAdded: selectedOption.isAppleCare,
      currentSelectedOptionId: selectedOption.id,
    };
  }

  /**
   * Calculate total price including AppleCare
   */
  calculateTotalPrice(basePrice: number, selectedAppleCareOption: AppleCareOption | null): number {
    const appleCarePrice = selectedAppleCareOption?.isAppleCare
      ? Number.parseFloat(selectedAppleCareOption.price.replace('$', ''))
      : 0;

    return basePrice + appleCarePrice;
  }

  /**
   * Get AppleCare price from selected option
   */
  getAppleCarePrice(selectedAppleCareOption: AppleCareOption | null): number {
    return selectedAppleCareOption?.isAppleCare ? Number.parseFloat(selectedAppleCareOption.price.replace('$', '')) : 0;
  }

  /**
   * Check if AppleCare is selected
   */
  isAppleCareSelected(selectedAppleCareOption: AppleCareOption | null): boolean {
    return selectedAppleCareOption?.isAppleCare || false;
  }

  /**
   * Get AppleCare features for display
   */
  getAppleCareFeatures(selectedAppleCareOption: AppleCareOption | null): string[] {
    return selectedAppleCareOption?.features || [];
  }

  /**
   * Validate AppleCare selection (used for form validation)
   */
  isAppleCareSelectionValid(hasAppleCare: boolean, selectedAppleCareOption: AppleCareOption | null): boolean {
    // If product has AppleCare available, user must make a selection
    if (hasAppleCare) {
      return selectedAppleCareOption !== null;
    }
    return true;
  }

  /**
   * Get AppleCare option by ID
   */
  getAppleCareOptionById(options: AppleCareOption[], id: string): AppleCareOption | null {
    return options.find((option) => option.id === id) || null;
  }

  /**
   * Format AppleCare price for display
   */
  formatAppleCarePrice(price: string): string {
    return price.startsWith('$') ? price : `$${price}`;
  }

  /**
   * Clear all caches
   */
  clearAllCaches(): void {
    const count = this.appleCareCache.size;
    this.appleCareCache.clear();
  }
}
